import { useState } from "react";
import {
  BookOpen,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import {
  useAcceptSuggestion,
  useCreateKnowledgeNote,
  useDeleteKnowledgeNote,
  useKnowledgeNotes,
  useRejectSuggestion,
  useUpdateKnowledgeNote,
} from "../api/aronKnowledgeApi";
import type { AronKnowledgeNote, AronKnowledgeNoteInput } from "../types";
import { AronMarkdown } from "../components/AronMarkdown";

type Tab = "active" | "pending";

const EMPTY: AronKnowledgeNoteInput = { title: "", short: "", full: "", tags: [] };

const SOURCE_STYLE: Record<string, string> = {
  seed: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  user: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  aron: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  code: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
  meta: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
};

// Source filter chips (active tab). CODE notes flood the list once the code-knowledge
// sync runs, so let the CEO narrow by origin.
const SOURCE_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "Wszystkie" },
  { value: "user", label: "Ręczne" },
  { value: "seed", label: "Z dokumentu" },
  { value: "code", label: "Z kodu" },
  { value: "meta", label: "META-ARON" },
  { value: "aron", label: "Od ARON" },
];

function SourceBadge({ note }: { note: AronKnowledgeNote }) {
  return (
    <span
      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${
        SOURCE_STYLE[note.source] ?? SOURCE_STYLE.user
      }`}
    >
      {(note.source === "aron" || note.source === "meta") && (
        <Sparkles size={11} className="mr-1" />
      )}
      {note.source_display}
    </span>
  );
}

function Tags({ tags }: { tags: string[] }) {
  if (!tags?.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {tags.map((t) => (
        <span
          key={t}
          className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500 dark:bg-gray-700 dark:text-gray-400"
        >
          #{t}
        </span>
      ))}
    </div>
  );
}

const INPUT =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 " +
  "dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100";

function NoteEditor({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
}: {
  initial: AronKnowledgeNoteInput;
  submitLabel: string;
  onSubmit: (input: AronKnowledgeNoteInput) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [title, setTitle] = useState(initial.title);
  const [short, setShort] = useState(initial.short);
  const [full, setFull] = useState(initial.full);
  const [tagsStr, setTagsStr] = useState((initial.tags ?? []).join(", "));

  const canSubmit = title.trim() && full.trim() && !busy;

  function submit() {
    if (!canSubmit) return;
    onSubmit({
      title: title.trim(),
      short: short.trim(),
      full: full.trim(),
      tags: tagsStr
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    });
  }

  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-800/40">
      <div>
        <label className="mb-0.5 block text-xs text-gray-500">Tytuł *</label>
        <input className={INPUT} value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div>
        <label className="mb-0.5 block text-xs text-gray-500">
          Skrót (1 linia — do wyszukiwania)
        </label>
        <input className={INPUT} value={short} onChange={(e) => setShort(e.target.value)} />
      </div>
      <div>
        <label className="mb-0.5 block text-xs text-gray-500">Treść (markdown) *</label>
        <textarea
          className={`${INPUT} min-h-[140px] font-mono text-xs`}
          value={full}
          onChange={(e) => setFull(e.target.value)}
        />
      </div>
      <div>
        <label className="mb-0.5 block text-xs text-gray-500">Tagi (po przecinku)</label>
        <input className={INPUT} value={tagsStr} onChange={(e) => setTagsStr(e.target.value)} />
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <X size={14} /> Anuluj
        </button>
        <button
          onClick={submit}
          disabled={!canSubmit}
          className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {submitLabel}
        </button>
      </div>
    </div>
  );
}

function ActiveCard({ note }: { note: AronKnowledgeNote }) {
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const update = useUpdateKnowledgeNote();
  const remove = useDeleteKnowledgeNote();

  if (editing) {
    return (
      <NoteEditor
        initial={{ title: note.title, short: note.short, full: note.full, tags: note.tags }}
        submitLabel="Zapisz"
        busy={update.isPending}
        onCancel={() => setEditing(false)}
        onSubmit={(input) =>
          update.mutate({ id: note.id, ...input }, { onSuccess: () => setEditing(false) })
        }
      />
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between gap-2">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-start gap-1.5 text-left"
        >
          {open ? (
            <ChevronDown size={16} className="mt-0.5 shrink-0 text-gray-400" />
          ) : (
            <ChevronRight size={16} className="mt-0.5 shrink-0 text-gray-400" />
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-gray-100">{note.title}</span>
              <SourceBadge note={note} />
            </div>
            <p className="mt-0.5 truncate text-xs text-gray-500">{note.short}</p>
          </div>
        </button>
        <div className="flex shrink-0 gap-1">
          <button
            onClick={() => setEditing(true)}
            title="Edytuj"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
          >
            <Pencil size={15} />
          </button>
          <button
            onClick={() => {
              if (window.confirm(`Usunąć notatkę „${note.title}"?`)) remove.mutate(note.id);
            }}
            title="Usuń"
            className="rounded-lg p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
          >
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
          <AronMarkdown text={note.full} />
          <Tags tags={note.tags} />
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ note }: { note: AronKnowledgeNote }) {
  const [editing, setEditing] = useState(false);
  const accept = useAcceptSuggestion();
  const reject = useRejectSuggestion();
  const busy = accept.isPending || reject.isPending;

  if (editing) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50/40 p-3 dark:border-red-900/40 dark:bg-red-900/10">
        <p className="mb-2 text-xs font-medium text-red-700 dark:text-red-300">
          Edytuj i zaakceptuj sugestię
        </p>
        <NoteEditor
          initial={{ title: note.title, short: note.short, full: note.full, tags: note.tags }}
          submitLabel="Zaakceptuj"
          busy={accept.isPending}
          onCancel={() => setEditing(false)}
          onSubmit={(edit) =>
            accept.mutate({ id: note.id, edit }, { onSuccess: () => setEditing(false) })
          }
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-red-200 bg-white p-3 dark:border-red-900/40 dark:bg-gray-800">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles size={14} className="text-red-500" />
        <span className="font-medium text-gray-900 dark:text-gray-100">{note.title}</span>
        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          Sugestia ARON
        </span>
      </div>
      {note.short && <p className="mt-1 text-xs text-gray-500">{note.short}</p>}
      <div className="mt-2 rounded-lg bg-gray-50 p-2 dark:bg-gray-900/40">
        <AronMarkdown text={note.full} />
        <Tags tags={note.tags} />
      </div>
      {note.why && (
        <p className="mt-2 text-xs italic text-gray-400">
          Dlaczego ARON to proponuje: {note.why}
          {note.suggested_in_title ? ` · z rozmowy „${note.suggested_in_title}"` : ""}
        </p>
      )}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <button
          onClick={() => reject.mutate(note.id)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <X size={14} /> Odrzuć
        </button>
        <button
          onClick={() => setEditing(true)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:hover:bg-red-900/30"
        >
          <Pencil size={14} /> Edytuj i akceptuj
        </button>
        <button
          onClick={() => accept.mutate({ id: note.id })}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
        >
          {accept.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Check size={14} />
          )}
          Akceptuj
        </button>
      </div>
    </div>
  );
}

export function AronKnowledgePage() {
  const [tab, setTab] = useState<Tab>("active");
  const [adding, setAdding] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const active = useKnowledgeNotes("active");
  const pending = useKnowledgeNotes("pending");
  const create = useCreateKnowledgeNote();

  const pendingCount = pending.data?.count ?? 0;
  const activeNotes = (active.data?.notes ?? []).filter(
    (n) => sourceFilter === "all" || n.source === sourceFilter,
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 via-red-700 to-black text-white">
          <BookOpen size={16} />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Baza wiedzy ARON</h1>
          <p className="text-xs text-gray-500">
            Wiedza domenowa, z której korzysta asystent ARON. Dodawaj własne notatki i
            zatwierdzaj te, które ARON proponuje z rozmów.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setTab("active")}
          className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
            tab === "active"
              ? "border-red-600 text-red-600"
              : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          }`}
        >
          Aktywne {active.data ? `(${active.data.count})` : ""}
        </button>
        <button
          onClick={() => setTab("pending")}
          className={`-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium ${
            tab === "pending"
              ? "border-red-600 text-red-600"
              : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
          }`}
        >
          Sugestie ARON
          {pendingCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 text-[11px] font-semibold text-white">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {tab === "active" && (
        <div className="space-y-3">
          {adding ? (
            <NoteEditor
              initial={EMPTY}
              submitLabel="Dodaj notatkę"
              busy={create.isPending}
              onCancel={() => setAdding(false)}
              onSubmit={(input) =>
                create.mutate(input, { onSuccess: () => setAdding(false) })
              }
            />
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:border-red-400 hover:text-red-600 dark:border-gray-600 dark:text-gray-300"
            >
              <Plus size={15} /> Dodaj notatkę
            </button>
          )}

          <div className="flex flex-wrap gap-1.5">
            {SOURCE_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setSourceFilter(f.value)}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  sourceFilter === f.value
                    ? "bg-red-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {active.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Wczytuję notatki…
            </div>
          ) : activeNotes.length > 0 ? (
            activeNotes.map((n) => <ActiveCard key={n.id} note={n} />)
          ) : (
            <p className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400 dark:border-gray-700">
              Brak aktywnych notatek{sourceFilter !== "all" ? " w tym źródle" : ""}.
            </p>
          )}
        </div>
      )}

      {tab === "pending" && (
        <div className="space-y-3">
          {pending.isLoading ? (
            <div className="flex items-center gap-2 p-4 text-sm text-gray-500">
              <Loader2 size={16} className="animate-spin" /> Wczytuję sugestie…
            </div>
          ) : pending.data && pending.data.notes.length > 0 ? (
            pending.data.notes.map((n) => <SuggestionCard key={n.id} note={n} />)
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center dark:border-gray-700">
              <Bot size={24} className="mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-400">
                Brak sugestii. ARON zaproponuje tu notatki, gdy w rozmowach pozna trwałą
                wiedzę o firmie.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
